import React from 'react';
import { Facebook, Instagram, Youtube, Mail, Phone, MapPin, Linkedin } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_d70cc816-e4d3-4f6e-bfaa-57b96c01f1a7/artifacts/w1f3hxo7_LTV-Production-LOGO-%281%29-white-2.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const socials = [
    { icon: Instagram, href: 'https://www.instagram.com/ltvproductionn', label: 'Instagram' },
    { icon: Facebook, href: 'https://www.facebook.com/ltvproductionn', label: 'Facebook' },
    { icon: Linkedin, href: 'https://www.linkedin.com/company/ltv-production-ltv-podcast-studio', label: 'LinkedIn' },
    { icon: Youtube, href: 'https://www.youtube.com/@LTVPRODUCTIONS', label: 'YouTube' },
  ];

  return (
    <footer className="relative z-10 bg-[#050505] text-white border-t border-white/[0.07]">
      {/* Big brand statement */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-16 sm:pt-20 pb-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 pb-14 border-b border-white/[0.07]">
          <div>
            <img src={LOGO_URL} alt="LTV Production" className="h-9 w-auto mb-5" />
            <p className="text-gray-400 leading-relaxed max-w-sm">
              The umbrella powering LTV Studio, LTV Carnival, LTV Live &amp; LTV On Demand — premium content creation across studios and on-location worldwide.
            </p>
          </div>
          <div className="flex gap-3">
            {socials.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="w-11 h-11 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.1] hover:border-white/25 flex items-center justify-center transition-all duration-300"
              >
                <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
              </a>
            ))}
          </div>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 py-14">
          <div>
            <h4 className="ltv-eyebrow text-gray-500 mb-5">Explore</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="/about" className="text-gray-400 hover:text-white transition-colors">About Us</a></li>
              <li><button onClick={() => handleNavigation('our-services')} className="text-gray-400 hover:text-white transition-colors text-left">Services</button></li>
              <li><button onClick={() => handleNavigation('featured-work')} className="text-gray-400 hover:text-white transition-colors text-left">Portfolio</button></li>
              <li><button onClick={() => handleNavigation('production-packages')} className="text-gray-400 hover:text-white transition-colors text-left">Packages</button></li>
              <li><a href="/contact" className="text-gray-400 hover:text-white transition-colors">Contact Us</a></li>
            </ul>
          </div>

          <div>
            <h4 className="ltv-eyebrow text-gray-500 mb-5">The LTV Universe</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="https://www.ltvstudio.co.uk" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-amber-400 transition-colors">LTV Studio London</a></li>
              <li><a href="/miami-coming-soon" className="text-gray-400 hover:text-sky-400 transition-colors">LTV Studio Miami <span className="text-gray-600">(Soon)</span></a></li>
              <li><a href="https://www.ltvcarnival.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-fuchsia-400 transition-colors">LTV Carnival</a></li>
              <li><a href="/live" className="text-gray-400 hover:text-red-400 transition-colors">LTV Live</a></li>
              <li><a href="/vod" className="text-gray-400 hover:text-purple-400 transition-colors">LTV On Demand</a></li>
              <li><a href="/on-location" className="text-gray-400 hover:text-emerald-400 transition-colors">On-Location Production</a></li>
            </ul>
          </div>

          <div className="col-span-2 lg:col-span-1">
            <h4 className="ltv-eyebrow text-gray-500 mb-5">Get in Touch</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3 text-gray-400">
                <Mail className="h-4 w-4 flex-shrink-0 mt-0.5 text-red-500" />
                <span>info@ltvproduction.com</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <Phone className="h-4 w-4 flex-shrink-0 mt-0.5 text-red-500" />
                <div className="flex flex-col gap-1">
                  <span>+44 (0) 208 064 1808</span>
                  <span>+1 (786) 926 5909</span>
                </div>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5 text-red-500" />
                <span>London, UK &amp; Miami, USA</span>
              </li>
            </ul>
          </div>

          <div className="col-span-2 lg:col-span-1">
            <h4 className="ltv-eyebrow text-gray-500 mb-5">Legal</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a></li>
              <li><a href="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a></li>
              <li><a href="/cookie-policy" className="text-gray-400 hover:text-white transition-colors">Cookie Policy</a></li>
              <li><a href="/dmca" className="text-gray-400 hover:text-white transition-colors">DMCA</a></li>
              <li><a href="/content-policy" className="text-gray-400 hover:text-white transition-colors">Content Policy</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/[0.07] pt-8 pb-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-600 text-xs">
            © {currentYear} LTV Production LTD. All rights reserved.
          </p>
          <p className="ltv-eyebrow text-gray-700">
            London — Miami — Worldwide
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
